import React, { useEffect, useState, useMemo, useCallback } from "react";
import { fmtBitRate } from "../utils/formatters";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { useWanName, WanLabel } from "./WanNames";
import {
  TimeSeriesChart,
  ArcGauge,
  MiniKPI,
  useTimeSeries,
} from "./DiagCharts";

// ── Helpers ──────────────────────────────────────────────────────────────────

const SLIDER_PRESETS = {
  latency:   { min: 0, max: 1000, step: 10, unit: "ms", label: "Latency" },
  jitter:    { min: 0, max: 500,  step: 5,  unit: "ms", label: "Jitter" },
  loss:      { min: 0, max: 100,  step: 0.5, unit: "%", label: "Packet Loss" },
  bandwidth: { min: 0, max: 100000, step: 100, unit: "kbps", label: "Bandwidth Cap" },
  corrupt:   { min: 0, max: 50,  step: 0.5, unit: "%", label: "Corruption" },
  duplicate: { min: 0, max: 50,  step: 0.5, unit: "%", label: "Duplication" },
  reorder:   { min: 0, max: 50,  step: 0.5, unit: "%", label: "Reordering" },
};

// A cap kept in kbps, read in the unit it reaches: kbps, Mbps, Gbps.
const fmtKbps = (kbps) => fmtBitRate((Number(kbps) || 0) / 1000);

function ImpairmentSlider({ id, value, onChange, preset }) {
  const { min, max, step, unit, label } = preset;
  const pct = max > 0 ? ((value - min) / (max - min)) * 100 : 0;
  return (
    <div className="stress-slider-group">
      <div className="stress-slider-header">
        <label htmlFor={id}>{label}</label>
        <span className="stress-slider-value">
          {id === "bandwidth" ? (value === 0 ? "Unlimited" : fmtKbps(value)) : `${value} ${unit}`}
        </span>
      </div>
      <div className="stress-slider-track-wrap">
        <input
          id={id} type="range" min={min} max={max} step={step}
          value={value} onChange={(e) => onChange(Number(e.target.value))}
          className="stress-slider"
          style={{ "--fill-pct": `${pct}%` }}
        />
      </div>
    </div>
  );
}

/* When an impairment clears itself, as the appliance armed it: always on the
   last working line, which it may cut off from this console. "" for never. */
function clearsAt(imp) {
  const t = imp?.expires_at ? new Date(imp.expires_at) : null;
  return t && t.getFullYear() > 2000 ? t.toLocaleTimeString() : "";
}

function WANImpairmentCard({
  wan,
  activeImpairment,
  draft,
  setDraft,
  onApply,
  onClear,
  applying,
  clearing,
}) {
  const isImpaired = activeImpairment?.active;

  return (
    <div className={`stress-wan-card ${isImpaired ? "impaired" : ""}`}>
      <div className="stress-wan-header">
        <div className="stress-wan-name">
          <span className={`stress-wan-dot ${isImpaired ? "red" : "green"}`} />
          <WanLabel iface={wan.name} />
        </div>
        <div className="stress-wan-badge">
          {isImpaired ? (
            <span className="pill-err">IMPAIRED</span>
          ) : (
            <span className="pill-ok">CLEAN</span>
          )}
        </div>
      </div>

      {/* Active impairment summary */}
      {isImpaired && (
        <div className="stress-active-summary">
          {activeImpairment.latency_ms > 0 && (
            <span className="stress-tag">Latency: {activeImpairment.latency_ms}ms</span>
          )}
          {activeImpairment.jitter_ms > 0 && (
            <span className="stress-tag">Jitter: ±{activeImpairment.jitter_ms}ms</span>
          )}
          {activeImpairment.loss_pct > 0 && (
            <span className="stress-tag">Loss: {activeImpairment.loss_pct}%</span>
          )}
          {activeImpairment.bandwidth_kbps > 0 && (
            <span className="stress-tag">BW Cap: {fmtKbps(activeImpairment.bandwidth_kbps)}</span>
          )}
          {activeImpairment.corrupt_pct > 0 && (
            <span className="stress-tag">Corrupt: {activeImpairment.corrupt_pct}%</span>
          )}
          {activeImpairment.duplicate_pct > 0 && (
            <span className="stress-tag">Dup: {activeImpairment.duplicate_pct}%</span>
          )}
          {activeImpairment.reorder_pct > 0 && (
            <span className="stress-tag">Reorder: {activeImpairment.reorder_pct}%</span>
          )}
          {clearsAt(activeImpairment) && (
            <span className="stress-tag">Clears itself at {clearsAt(activeImpairment)}</span>
          )}
        </div>
      )}

      {/* Sliders */}
      <div className="stress-sliders">
        <ImpairmentSlider
          id="latency" preset={SLIDER_PRESETS.latency}
          value={draft.latency_ms}
          onChange={(v) => setDraft({ ...draft, latency_ms: v })}
        />
        <ImpairmentSlider
          id="jitter" preset={SLIDER_PRESETS.jitter}
          value={draft.jitter_ms}
          onChange={(v) => setDraft({ ...draft, jitter_ms: v })}
        />
        <ImpairmentSlider
          id="loss" preset={SLIDER_PRESETS.loss}
          value={draft.loss_pct}
          onChange={(v) => setDraft({ ...draft, loss_pct: v })}
        />
        <ImpairmentSlider
          id="bandwidth" preset={SLIDER_PRESETS.bandwidth}
          value={draft.bandwidth_kbps}
          onChange={(v) => setDraft({ ...draft, bandwidth_kbps: v })}
        />
        <details className="stress-advanced-toggle">
          <summary>Advanced impairments</summary>
          <div className="stress-advanced-sliders">
            <ImpairmentSlider
              id="corrupt" preset={SLIDER_PRESETS.corrupt}
              value={draft.corrupt_pct}
              onChange={(v) => setDraft({ ...draft, corrupt_pct: v })}
            />
            <ImpairmentSlider
              id="duplicate" preset={SLIDER_PRESETS.duplicate}
              value={draft.duplicate_pct}
              onChange={(v) => setDraft({ ...draft, duplicate_pct: v })}
            />
            <ImpairmentSlider
              id="reorder" preset={SLIDER_PRESETS.reorder}
              value={draft.reorder_pct}
              onChange={(v) => setDraft({ ...draft, reorder_pct: v })}
            />
          </div>
        </details>
      </div>

      {/* Actions */}
      <div className="stress-wan-actions">
        <button
          className="primary-btn"
          onClick={() => onApply(wan.name, draft)}
          disabled={applying}
        >
          {applying ? "Applying…" : "Apply Impairment"}
        </button>
        <button
          className="danger-btn"
          onClick={() => onClear(wan.name)}
          disabled={clearing || !isImpaired}
        >
          {clearing ? "Clearing…" : "Clear"}
        </button>
      </div>
    </div>
  );
}

function ScenarioCard({ scenario, wans, onApply, applying }) {
  const wanName = useWanName();
  return (
    <div className="stress-scenario-card">
      <div className="stress-scenario-name">{scenario.name}</div>
      <div className="stress-scenario-desc">{scenario.description}</div>
      {scenario.impairments && (
        <div className="stress-scenario-preview">
          {scenario.impairments.map((imp, i) => (
            <div key={i} className="stress-scenario-imp">
              <span className="stress-scenario-wan-label">
                {wans[i]?.name ? wanName(wans[i].name) : `WAN ${i + 1}`}:
              </span>
              {imp.latency_ms > 0 && <span className="stress-tag sm">{imp.latency_ms}ms</span>}
              {imp.jitter_ms > 0 && <span className="stress-tag sm">±{imp.jitter_ms}ms</span>}
              {imp.loss_pct > 0 && <span className="stress-tag sm">{imp.loss_pct}% loss</span>}
              {imp.bandwidth_kbps > 0 && <span className="stress-tag sm">{fmtKbps(imp.bandwidth_kbps)}</span>}
              {!imp.latency_ms && !imp.loss_pct && !imp.bandwidth_kbps && !imp.jitter_ms && (
                <span className="stress-tag sm clean">Clean</span>
              )}
            </div>
          ))}
        </div>
      )}
      <button
        className="primary-btn sm"
        onClick={() => onApply(scenario.name)}
        disabled={applying}
      >
        {applying ? "Applying…" : "Run Scenario"}
      </button>
    </div>
  );
}

function EventLog({ events }) {
  if (!events || events.length === 0) {
    return <div className="stress-events-empty">No stress test events yet.</div>;
  }
  return (
    <div className="stress-events-list">
      {events.slice().reverse().map((ev, i) => (
        <div key={i} className="stress-event-row">
          <span className={`stress-event-action ${ev.action}`}>{ev.action.toUpperCase()}</span>
          {ev.wan && <span className="stress-event-wan">{ev.wan}</span>}
          <span className="stress-event-details">{ev.details}</span>
          <span className="stress-event-time">
            {ev.time ? new Date(ev.time).toLocaleTimeString() : ""}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Main Panel ───────────────────────────────────────────────────────────────

const EMPTY_DRAFT = {
  latency_ms: 0,
  jitter_ms: 0,
  loss_pct: 0,
  bandwidth_kbps: 0,
  corrupt_pct: 0,
  duplicate_pct: 0,
  reorder_pct: 0,
};

export default function StressTestPanel({ nodeApiUrl, selectedNode }) {
  const wanName = useWanName();
  const [wans, setWans] = useState([]);
  const [impairments, setImpairments] = useState({});
  const [scenarios, setScenarios] = useState([]);
  const [events, setEvents] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [applyingWan, setApplyingWan] = useState("");
  const [clearingWan, setClearingWan] = useState("");
  const [applyingScenario, setApplyingScenario] = useState("");
  const [clearingAll, setClearingAll] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("manual"); // "manual" | "scenarios" | "events" | "impact"
  const [healthData, setHealthData] = useState(null);
  const ts = useTimeSeries(60);

  const fetchJson = useCallback(async (path, options) => {
    const url = nodeApiUrl(path);
    if (!url) throw new Error("No node selected");
    const res = await fetchWithTimeout(url, options);
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `${res.status}`);
    }
    if (res.status === 204) return null;
    return res.json();
  }, [nodeApiUrl]);

  // Poll status
  useEffect(() => {
    if (!selectedNode) return;
    const controller = new AbortController();
    const poll = async () => {
      try {
        const [statusRes, wansRes, scenRes, evRes, hRes] = await Promise.all([
          fetchJson("/stress/status"),
          fetchJson("/wans"),
          fetchJson("/stress/scenarios"),
          fetchJson("/stress/events"),
          fetchJson("/health").catch(() => null),
        ]);
        if (!controller.signal.aborted) {
          setImpairments(statusRes?.impairments || {});
          const wanList = Array.isArray(wansRes?.wans) ? wansRes.wans : [];
          setWans(wanList);
          setScenarios(scenRes?.scenarios || []);
          setEvents(evRes?.events || []);
          if (hRes) {
            setHealthData(hRes);
            for (const w of hRes.health || []) {
              const n = w.wan || w.name;
              ts.push(n + "_lat", w.latency_ms ?? 0);
              ts.push(n + "_jit", w.jitter_ms ?? 0);
              ts.push(n + "_loss", (w.loss_ratio ?? 0) * 100);
              ts.push(n + "_score", w.score_balanced ?? 0);
            }
          }
          // Initialize drafts for new WANs
          setDrafts((prev) => {
            const next = { ...prev };
            for (const w of wanList) {
              if (!next[w.name]) next[w.name] = { ...EMPTY_DRAFT };
            }
            return next;
          });
          setError("");
        }
      } catch (err) {
        if (!controller.signal.aborted) setError(err.message);
      }
    };
    poll();
    const timer = setInterval(poll, 3000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [selectedNode, fetchJson]);

  const setDraftForWan = useCallback((wan, draft) => {
    setDrafts((prev) => ({ ...prev, [wan]: draft }));
  }, []);

  const handleApply = useCallback(async (wan, draft) => {
    setApplyingWan(wan);
    try {
      await fetchJson("/stress/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wan, ...draft }),
      });
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setApplyingWan("");
    }
  }, [fetchJson]);

  const handleClear = useCallback(async (wan) => {
    setClearingWan(wan);
    try {
      await fetchJson("/stress/clear", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wan }),
      });
      setDrafts((prev) => ({ ...prev, [wan]: { ...EMPTY_DRAFT } }));
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setClearingWan("");
    }
  }, [fetchJson]);

  const handleClearAll = useCallback(async () => {
    setClearingAll(true);
    try {
      await fetchJson("/stress/clear-all", { method: "POST" });
      setDrafts((prev) => {
        const next = {};
        for (const k of Object.keys(prev)) next[k] = { ...EMPTY_DRAFT };
        return next;
      });
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setClearingAll(false);
    }
  }, [fetchJson]);

  const handleScenario = useCallback(async (name) => {
    setApplyingScenario(name);
    try {
      await fetchJson("/stress/scenario/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, wans: wans.map((w) => w.name) }),
      });
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setApplyingScenario("");
    }
  }, [fetchJson, wans]);

  const anyImpaired = useMemo(
    () => Object.values(impairments).some((i) => i?.active),
    [impairments]
  );

  if (!selectedNode) {
    return (
      <div className="panel stress-panel">
        <div className="panel-title">WAN Stress Tester</div>
        <div className="panel-sub">Select an edge node to begin stress testing.</div>
      </div>
    );
  }

  return (
    <div className="panel stress-panel">
      <div className="stress-panel-header">
        <div>
          <div className="panel-title">WAN Stress Tester</div>
          <div className="panel-sub">
            Inject real network impairments (latency, loss, bandwidth limits) on individual WAN
            interfaces to demonstrate QoS-driven failover in real time.
          </div>
        </div>
        {anyImpaired && (
          <button
            className="danger-btn"
            onClick={handleClearAll}
            disabled={clearingAll}
          >
            {clearingAll ? "Clearing…" : "Clear All Impairments"}
          </button>
        )}
      </div>

      {error && <div className="panel-error">{error}</div>}

      {/* Tab bar */}
      <div className="stress-tabs">
        <button
          className={`stress-tab ${activeTab === "manual" ? "active" : ""}`}
          onClick={() => setActiveTab("manual")}
        >
          Manual Control
        </button>
        <button
          className={`stress-tab ${activeTab === "scenarios" ? "active" : ""}`}
          onClick={() => setActiveTab("scenarios")}
        >
          Scenarios ({scenarios.length})
        </button>
        <button
          className={`stress-tab ${activeTab === "events" ? "active" : ""}`}
          onClick={() => setActiveTab("events")}
        >
          Event Log ({events.length})
        </button>
        <button
          className={`stress-tab ${activeTab === "impact" ? "active" : ""}`}
          onClick={() => setActiveTab("impact")}
        >
          Live Impact
        </button>
      </div>

      {/* Manual tab */}
      {activeTab === "manual" && (
        <div className="stress-wan-grid">
          {wans.map((wan) => (
            <WANImpairmentCard
              key={wan.name}
              wan={wan}
              activeImpairment={impairments[wan.name]}
              draft={drafts[wan.name] || { ...EMPTY_DRAFT }}
              setDraft={(d) => setDraftForWan(wan.name, d)}
              onApply={handleApply}
              onClear={handleClear}
              applying={applyingWan === wan.name}
              clearing={clearingWan === wan.name}
            />
          ))}
          {wans.length === 0 && (
            <div className="stress-empty">No WAN interfaces detected on this edge node.</div>
          )}
        </div>
      )}

      {/* Scenarios tab */}
      {activeTab === "scenarios" && (
        <div className="stress-scenarios-grid">
          {scenarios.map((s) => (
            <ScenarioCard
              key={s.name}
              scenario={s}
              wans={wans}
              onApply={handleScenario}
              applying={applyingScenario === s.name}
            />
          ))}
        </div>
      )}

      {/* Events tab */}
      {activeTab === "events" && <EventLog events={events} />}

      {/* Live Impact tab */}
      {activeTab === "impact" && (
        <div className="diag-dashboard">
          {/* Gauges */}
          {healthData?.health && healthData.health.length > 0 && (
            <div className="diag-row cols-2">
              {healthData.health.map((w) => {
                const n = w.wan || w.name;
                const imp = impairments[n];
                return (
                  <div key={n} className="diag-card" style={imp?.active ? { borderColor: "#ef4444" } : {}}>
                    <div className="diag-card-title">
                      {n} {imp?.active ? "(IMPAIRED)" : ""}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 8 }}>
                      <ArcGauge value={Math.round(w.score_balanced ?? 0)} max={100} label="Score" size={110} />
                    </div>
                    <div className="diag-kpi-strip">
                      <MiniKPI label="Latency" value={w.latency_ms != null ? w.latency_ms.toFixed(1) : "\u2014"} unit=" ms" sparkData={ts.get(n + "_lat")} color={w.latency_ms > 100 ? "#ef4444" : "#22c55e"} />
                      <MiniKPI label="Loss" value={w.loss_ratio != null ? (w.loss_ratio * 100).toFixed(1) : "\u2014"} unit="%" sparkData={ts.get(n + "_loss")} color={w.loss_ratio > 0.05 ? "#ef4444" : "#22c55e"} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Latency comparison chart */}
          {wans.length > 0 && (
            <div className="diag-card">
              <div className="diag-card-title">Latency Impact (3 min)</div>
              <TimeSeriesChart
                series={wans.map((w, i) => ({
                  label: wanName(w.name) + " latency",
                  data: ts.get(w.name + "_lat"),
                  color: i === 0 ? "#6366f1" : "#06b6d4",
                }))}
                yLabel="ms"
                yMin={0}
                height={200}
              />
            </div>
          )}

          {/* Score comparison chart */}
          {wans.length > 0 && (
            <div className="diag-card">
              <div className="diag-card-title">Health Score Impact (3 min)</div>
              <TimeSeriesChart
                series={wans.map((w, i) => ({
                  label: wanName(w.name) + " score",
                  data: ts.get(w.name + "_score"),
                  color: i === 0 ? "#22c55e" : "#3b82f6",
                }))}
                yLabel="Score"
                yMin={0}
                yMax={100}
                height={200}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
